import React, { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, FileText, Menu, X, Settings, LogOut, Sparkles } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';

const CreateDispute = () => {
  const { clientId } = useParams();
  const [searchParams] = useSearchParams();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    bureau: '',
    account_name: '',
    account_number: '',
    dispute_reason: ''
  });

  useEffect(() => {
    if (clientId) {
      fetchClientData();
    } else {
      setLoading(false);
    }
  }, [clientId]);

  const fetchClientData = async () => {
    try {
      const res = await axios.get(`${API}/broker/clients/${clientId}`);
      setClient(res.data);
    } catch (error) {
      toast.error('Failed to load client data');
      navigate('/broker/clients');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.bureau || !formData.account_name || !formData.account_number || !formData.dispute_reason) {
      toast.error('Please fill in all fields');
      return;
    }

    setSubmitting(true);
    try {
      const disputeData = {
        ...formData,
        client_id: clientId || null
      };
      
      await axios.post(`${API}/disputes`, disputeData);
      toast.success('Dispute created successfully!');
      
      if (clientId) {
        navigate(`/broker/clients/${clientId}`);
      } else {
        navigate('/broker');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create dispute');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl font-heading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <span className="text-2xl font-heading font-bold text-gradient">DISPUTEKING.AI</span>
              <span className="block text-xs text-slate-600">Broker Portal</span>
            </div>
            
            <div className="hidden md:flex items-center gap-6">
              <button onClick={() => navigate('/broker')} className="text-slate-600 hover:text-violet-600 font-medium transition-colors">Dashboard</button>
              <button onClick={() => navigate('/broker/clients')} className="text-violet-600 font-medium">Clients</button>
              <button onClick={() => navigate('/education')} className="text-slate-600 hover:text-violet-600 font-medium transition-colors">Resources</button>
              <button onClick={() => navigate('/settings')} className="text-slate-600 hover:text-violet-600 font-medium transition-colors">
                <Settings className="w-5 h-5" />
              </button>
              <Button onClick={() => { logout(); navigate('/'); }} variant="ghost" className="font-heading">
                <LogOut className="w-5 h-5 mr-2" />
                Logout
              </Button>
            </div>

            <button className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-4 mb-8">
          <Button
            onClick={() => navigate(clientId ? `/broker/clients/${clientId}` : '/broker')}
            variant="ghost"
            className="rounded-full"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </Button>
        </div>

        <div className="bg-white rounded-3xl p-8 border-2 border-slate-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-2xl bg-violet-100 flex items-center justify-center">
              <FileText className="w-6 h-6 text-violet-600" />
            </div>
            <div>
              <h1 className="text-2xl font-heading font-bold">Create New Dispute</h1>
              {client && (
                <p className="text-slate-600">For client: {client.client_name}</p>
              )}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Bureau Selection */}
            <div>
              <Label htmlFor="bureau">Credit Bureau *</Label>
              <Select 
                value={formData.bureau} 
                onValueChange={(value) => setFormData({ ...formData, bureau: value })}
              >
                <SelectTrigger className="rounded-xl" id="bureau">
                  <SelectValue placeholder="Select credit bureau" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Experian">Experian</SelectItem>
                  <SelectItem value="Equifax">Equifax</SelectItem>
                  <SelectItem value="TransUnion">TransUnion</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Account Name */}
            <div>
              <Label htmlFor="account_name">Account Name *</Label>
              <Input
                id="account_name"
                value={formData.account_name}
                onChange={(e) => setFormData({ ...formData, account_name: e.target.value })}
                placeholder="e.g., Capital One, Discover, etc."
                className="rounded-xl"
                required
              />
              <p className="text-sm text-slate-500 mt-1">The name of the creditor or account</p>
            </div>

            {/* Account Number */}
            <div>
              <Label htmlFor="account_number">Account Number *</Label>
              <Input
                id="account_number"
                value={formData.account_number}
                onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                placeholder="Last 4 digits or full account number"
                className="rounded-xl"
                required
              />
              <p className="text-sm text-slate-500 mt-1">Enter the account number as it appears on the credit report</p>
            </div>

            {/* Dispute Reason */}
            <div>
              <Label htmlFor="dispute_reason">Dispute Reason *</Label>
              <Textarea
                id="dispute_reason"
                value={formData.dispute_reason}
                onChange={(e) => setFormData({ ...formData, dispute_reason: e.target.value })}
                placeholder="Describe why you are disputing this item. Be specific about what is incorrect or needs verification."
                className="rounded-xl min-h-[150px]"
                required
              />
              <p className="text-sm text-slate-500 mt-1">
                Common reasons: Not mine, Incorrect balance, Already paid, Account closed, etc.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 pt-4">
              <Button
                type="button"
                onClick={() => navigate(clientId ? `/broker/clients/${clientId}` : '/broker')}
                variant="outline"
                className="rounded-full flex-1 font-heading"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-violet-600 hover:bg-violet-700 text-white rounded-full flex-1 font-heading"
              >
                {submitting ? (
                  <>
                    <Sparkles className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <FileText className="w-4 h-4 mr-2" />
                    Create Dispute
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>

        {/* Information Card */}
        <div className="mt-6 bg-blue-50 rounded-2xl p-6 border border-blue-100">
          <h3 className="font-heading font-semibold text-blue-900 mb-2">💡 Tip</h3>
          <p className="text-sm text-blue-800">
            Disputes are most effective when they are specific and factual. Focus on verifiable errors rather than opinions. 
            The dispute will be saved as a draft and can be reviewed before submission.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CreateDispute;
